import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

/**
 * Drives the bottom reply composer for the interactions thread.
 *
 * The composer replies into the contact's most recent open conversation by
 * reusing the real ReplyBox, which is driven entirely by the selected chat.
 * To avoid marking a conversation as read just from viewing it, the target is
 * only made the active chat once the user activates the composer (focus). The
 * selected-chat state is reset whenever the target changes and on unmount so it
 * never leaks into the rest of the app.
 *
 * @param {import('vue').Ref<Array>} conversations - Loaded conversations (oldest-first).
 */
export function useInteractionComposer(conversations) {
  const store = useStore();
  const inboxGetter = useMapGetter('inboxes/getInbox');
  const currentChat = useMapGetter('getSelectedChat');

  const isActivated = ref(false);

  const replyTarget = computed(
    () =>
      [...conversations.value].reverse().find(c => c.status === 'open') || null
  );
  const replyTargetInboxName = computed(
    () => inboxGetter.value(replyTarget.value?.inbox_id)?.name || ''
  );
  const isComposerActive = computed(
    () =>
      isActivated.value &&
      !!replyTarget.value &&
      currentChat.value?.id === replyTarget.value.id
  );

  const activate = () => {
    const target = replyTarget.value;
    if (!target?.messages?.length) return;
    store.dispatch('setActiveChat', { data: target });
    isActivated.value = true;
  };

  const reset = () => {
    if (!isActivated.value) return;
    isActivated.value = false;
    store.dispatch('clearSelectedState');
  };

  // Drop back to the inactive placeholder whenever the target changes
  // (contact switch, the newest open conversation changes, etc.).
  watch(() => replyTarget.value?.id ?? null, reset);

  onBeforeUnmount(reset);

  return {
    replyTarget,
    replyTargetInboxName,
    isActivated,
    isComposerActive,
    activate,
  };
}
